import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

/**
 * Reusable Form Dialog Component
 * 
 * Usage:
 * <FormDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Edit User"
 *   description="Update user information"
 *   onSubmit={handleSubmit}
 *   submitText="Save Changes"
 *   maxWidth="lg"
 * >
 *   <form>
 *     <Input name="name" label="Name" />
 *     <Input name="email" label="Email" />
 *   </form>
 * </FormDialog>
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  onCancel,
  submitText = "Save",
  cancelText = "Cancel",
  loading = false,
  disabled = false,
  maxWidth = "md", // "sm" | "md" | "lg" | "xl" | "2xl"
  scrollable = false,
  children,
  footer,
  hideFooter = false
}) {
  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit?.();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange?.(false);
    }
  };

  const maxWidthClass = {
    sm: "sm:max-w-[425px]",
    md: "sm:max-w-[600px]",
    lg: "sm:max-w-[800px]",
    xl: "sm:max-w-[1000px]",
    "2xl": "sm:max-w-[1200px]"
  }[maxWidth] || "sm:max-w-[600px]";

  const content = scrollable ? (
    <ScrollArea className="max-h-[60vh] pr-4">
      {children}
    </ScrollArea>
  ) : children;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={maxWidthClass}>
        <DialogHeader>
          <DialogTitle className="text-brand-dark">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-brand-dark/70">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="py-4">
            {content}
          </div>

          {!hideFooter && (
            <DialogFooter className="gap-2 sm:gap-0">
              {footer ? footer : (
                <>
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {cancelText}
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || disabled}
                    variant="secondary"
                  >
                    {loading ? 'Saving...' : submitText}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default FormDialog;
